import { MainRoutes } from '@/utils/routes'
import { Link } from '@/components/ui/deprecated-link'
import { InviteLink } from '@/components/invite-link'
import { useInviteLink } from '@/modules/invite/use-invite-link'
import { SignInGoogleButton } from '@/components/sign-in-google-button'
import { SalaryRestrictionType } from '@/modules/salary/salary-table-context'
import { cn } from '@/lib/css-utils'
import { useG2GTracking } from '@/modules/salary/analytics/use-g2g-tracking'
import { useIntersectionObserver } from '@/modules/salary/analytics/use-intersection-observer'
import { useSignupTracking } from '@/modules/salary/analytics/use-signup-tracking'

interface TotalCompAlertProps {
  salaryRestriction: SalaryRestrictionType | null
  containerHeight?: number
  descriptionClassName?: string
  salaryId?: string
  moduleName?: 'histogram' | 'pane'
  isAuthenticated: boolean
}

function Description({
  descriptionClassName,
  children,
}: React.PropsWithChildren<{ descriptionClassName?: string }>) {
  return (
    <p className={cn('text-sm text-copy-accent mb-4', descriptionClassName)}>
      {children}
    </p>
  )
}

function PublicLimitLockAlert({
  descriptionClassName,
  moduleName,
  ref,
}: {
  descriptionClassName?: string
  moduleName: 'histogram' | 'pane'
  ref: React.Ref<HTMLAnchorElement>
}) {
  const { trackSignupClick } = useSignupTracking()
  return (
    <>
      <p className="font-bold">Want to see salary details?</p>
      <Description descriptionClassName={descriptionClassName}>
        Add your anonymous salary to unlock all the salaries shared by your
        peers.
      </Description>
      <Link
        ref={ref}
        href={MainRoutes.SignUp}
        variant="primaryButton"
        linkClassName="w-full"
        className="w-full"
        size="lg"
        aria-label={`${moduleName} unlock all salaries`}
        onClick={() => trackSignupClick(moduleName)}
      >
        Unlock All Salaries
      </Link>
      <div className="flex items-center justify-center mt-3 w-full">
        <div className="w-full h-px bg-background-divider" />
        <span className="px-4 text-copy-accent font-medium text-sm">OR</span>
        <div className="w-full h-px bg-background-divider" />
      </div>

      <SignInGoogleButton
        size="md"
        aria-label={`${moduleName} continue with google login`}
      />
    </>
  )
}

function ExpiredLockAlert({
  descriptionClassName,
  isAuthenticated,
}: {
  descriptionClassName?: string
  isAuthenticated: boolean
}) {
  const { inviteUrl } = useInviteLink({ isAuthenticated })
  return (
    <>
      <p className="font-bold">Want to see salary details?</p>
      <Description descriptionClassName={descriptionClassName}>
        Use your personalized link to invite a friend to join Marit and unlock 1
        year of access. The easiest way to share is via group text, email, or
        social media.
      </Description>
      <InviteLink
        inviteUrl={inviteUrl}
        loading={false}
        aria-label="invite a friend"
      />
    </>
  )
}

function ProfessionLockAlert({
  descriptionClassName,
}: {
  descriptionClassName?: string
}) {
  return (
    <>
      <p className="font-bold">Salary details have been hidden</p>
      <Description descriptionClassName={descriptionClassName}>
        These are only available for your profession
      </Description>
    </>
  )
}

function OnboardingInProgressLockAlert({
  descriptionClassName,
  moduleName,
  ref,
}: {
  descriptionClassName?: string
  moduleName: 'histogram' | 'pane'
  ref: React.Ref<HTMLAnchorElement | null>
}) {
  const { trackSignupClick } = useG2GTracking()
  return (
    <>
      <p className="font-bold">Want to see salary details?</p>
      <Description descriptionClassName={descriptionClassName}>
        We use a &#34;give-to-get&#34; model - please complete your account
        setup to unlock all the salaries shared by your peers.
      </Description>
      <Link
        ref={ref}
        href={MainRoutes.Onboarding}
        variant="primaryButton"
        onClick={() => trackSignupClick(moduleName)}
        size="lg"
        aria-label="complete account setup"
      >
        Complete Account Setup
      </Link>
    </>
  )
}

export function EmployerLockAlert({
  descriptionClassName,
}: {
  descriptionClassName?: string
}) {
  return (
    <>
      <p className="font-bold">Salary details have been hidden</p>
      <Description descriptionClassName={descriptionClassName}>
        Some salary information has been hidden - employers accounts have
        limited access to salary details.
      </Description>
    </>
  )
}

function hasSalaryRestriction(salaryRestriction: SalaryRestrictionType | null) {
  return (
    salaryRestriction &&
    Object.values(SalaryRestrictionType).includes(salaryRestriction)
  )
}

export function LockAlertContent({
  salaryRestriction,
  salaryId,
  descriptionClassName,
  moduleName = 'pane',
  isAuthenticated,
}: TotalCompAlertProps) {
  const { useG2GViewTracking } = useG2GTracking()
  const handleVisible = useG2GViewTracking({
    moduleName,
    salaryId,
  })

  const elementRef = useIntersectionObserver<HTMLAnchorElement>({
    onIntersect: handleVisible,
    triggerOnce: true,
  })
  if (salaryRestriction === SalaryRestrictionType.SignedOut) {
    return (
      <PublicLimitLockAlert
        ref={elementRef}
        descriptionClassName={descriptionClassName}
        moduleName={moduleName}
      />
    )
  } else if (
    salaryRestriction === SalaryRestrictionType.Expired ||
    salaryRestriction === SalaryRestrictionType.Student
  ) {
    return (
      <ExpiredLockAlert
        descriptionClassName={descriptionClassName}
        isAuthenticated={isAuthenticated}
      />
    )
  } else if (salaryRestriction === SalaryRestrictionType.MismatchedProfession) {
    return <ProfessionLockAlert descriptionClassName={descriptionClassName} />
  } else if (salaryRestriction === SalaryRestrictionType.OnboardingInProgress) {
    return (
      <OnboardingInProgressLockAlert
        ref={elementRef}
        descriptionClassName={descriptionClassName}
        moduleName={moduleName}
      />
    )
  } else if (salaryRestriction === SalaryRestrictionType.Employer) {
    return <EmployerLockAlert descriptionClassName={descriptionClassName} />
  }
  return null
}

export function TotalCompAlert({
  salaryRestriction,
  salaryId,
  moduleName,
  isAuthenticated,
  ...props
}: TotalCompAlertProps) {
  if (!hasSalaryRestriction(salaryRestriction)) {
    return null
  }
  return (
    <div
      className="absolute inset-0 flex px-8 text-center justify-center
     items-center w-full h-full"
      data-testid="salary-signup-alert"
    >
      <div className="h-fit flex flex-col gap-2 pt-4">
        <LockAlertContent
          salaryRestriction={salaryRestriction}
          salaryId={salaryId}
          descriptionClassName={props.descriptionClassName}
          moduleName={moduleName}
          isAuthenticated={isAuthenticated}
        />
      </div>
    </div>
  )
}
